import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Stack,
  Typography,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  deleteModemImagen,
  getModemImagen,
  postModemImagen,
  putModemImagen,
} from "../services/reqSolicitudes";

const MAX_SIZE = 5 * 1024 * 1024;

function extractUrl(data) {
  return (
    data?.imagen_url ||
    data?.url ||
    data?.path ||
    data?.data?.imagen_url ||
    data?.data?.url ||
    data?.data?.path ||
    ""
  );
}

const ModemImageManager = forwardRef(function ModemImageManager({
  cod,
  numSim,
  initialUrl = "",
  toAbsUrl = (u) => u,
  onChanged,
  onToast,
  editMode = false,
  hideSaveButton = false,
  onPendingChange,
  readOnly = false,
}, ref) {
  const [imageUrl, setImageUrl] = useState(initialUrl || "");
  const [editing, setEditing] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const hasImage = !!imageUrl;
  const isEditing = editMode || editing;
  const absoluteUrl = useMemo(() => toAbsUrl(imageUrl), [imageUrl, toAbsUrl]);

  useEffect(() => {
    setImageUrl(initialUrl || "");
    setEditing(false);
    setFile(null);
    setPreview("");
    setError("");
  }, [initialUrl, numSim]);

  useEffect(() => {
    if (!cod || !numSim) return;
    let alive = true;
    setLoading(true);
    getModemImagen(cod, numSim)
      .then(({ data }) => {
        if (!alive) return;
        const url = extractUrl(data);
        if (url) setImageUrl(url);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [cod, numSim]);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  useEffect(() => {
    onPendingChange?.({ hasFile: !!file, error });
  }, [error, file, onPendingChange]);

  const clearFile = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview("");
    setError("");
  };

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected) return;
    if (!selected.type.startsWith("image/")) {
      clearFile();
      setError("Solo se permiten archivos de imagen.");
      return;
    }
    if (selected.size > MAX_SIZE) {
      clearFile();
      setError("La imagen no debe superar los 5 MB.");
      return;
    }
    clearFile();
    setFile(selected);
    setPreview(URL.createObjectURL(selected));
  };

  const savePendingImage = async ({ notify = true } = {}) => {
    if (!cod || !numSim || !file) return false;

    setSaving(true);
    try {
      const { data } = hasImage
        ? await putModemImagen(cod, numSim, file)
        : await postModemImagen(cod, numSim, file);
      let nextUrl = extractUrl(data);
      if (!nextUrl) {
        const imageResponse = await getModemImagen(cod, numSim);
        nextUrl = extractUrl(imageResponse?.data);
      }
      if (nextUrl) setImageUrl(nextUrl);
      clearFile();
      setEditing(false);
      await onChanged?.();
      if (notify) {
        onToast?.({ sev: "success", msg: hasImage ? "Imagen actualizada" : "Imagen guardada" });
      }
      return true;
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || e?.message || "No se pudo guardar la imagen";
      setError(msg);
      throw new Error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      await savePendingImage();
    } catch {
      // El mensaje ya queda visible en el helper del componente.
    }
  };

  useImperativeHandle(ref, () => ({
    savePendingImage,
    clearPendingImage: clearFile,
    hasPendingImage: () => !!file,
    hasError: () => !!error,
  }));

  const handleDelete = async () => {
    if (!cod || !numSim) return;
    const ok = window.confirm("¿Eliminar imagen del módem?");
    if (!ok) return;
    try {
      setDeleting(true);
      await deleteModemImagen(cod, numSim);
      setImageUrl("");
      clearFile();
      setEditing(false);
      await onChanged?.();
      onToast?.({ sev: "success", msg: "Imagen eliminada" });
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e?.message || "No se pudo eliminar la imagen");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
        Imagen / Evidencia
      </Typography>

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">Cargando imagen...</Typography>
        </Stack>
      ) : readOnly && !hasImage ? (
        <Typography variant="body2" color="text.secondary">
          Sin imagen registrada
        </Typography>
      ) : hasImage && !isEditing ? (
        <Stack spacing={1}>
          <a href={absoluteUrl} target="_blank" rel="noreferrer">
            <img
              src={absoluteUrl}
              alt="evidencia"
              style={{ maxWidth: "100%", height: "auto", borderRadius: 8, display: "block" }}
            />
          </a>
          {!readOnly && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
                Editar imagen
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<DeleteOutlineIcon />}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Eliminando..." : "Eliminar"}
              </Button>
            </Stack>
          )}
        </Stack>
      ) : (
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <Button variant="outlined" startIcon={<PhotoCameraIcon />} component="label">
              Elegir imagen
              <input type="file" accept="image/*" hidden onChange={handleFileChange} />
            </Button>
            {file && (
              <Button variant="text" startIcon={<DeleteOutlineIcon />} onClick={clearFile}>
                Quitar
              </Button>
            )}
            {hasImage && !editMode && (
              <Button variant="text" onClick={() => { clearFile(); setEditing(false); }}>
                Cancelar
              </Button>
            )}
          </Stack>

          {error && <FormHelperText error>{error}</FormHelperText>}

          {preview && (
            <Box sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
              <img src={preview} alt="preview" style={{ display: "block", width: "100%", height: "auto" }} />
            </Box>
          )}

          {!hideSaveButton && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !!error || !file}
              sx={{ alignSelf: "flex-start" }}
            >
              {saving ? "Guardando..." : hasImage ? "Actualizar imagen" : "Guardar imagen"}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
});

export default ModemImageManager;
